import { Plus, AlertTriangle, CircleDot } from 'lucide-react';
import ListToolbar, { PohledPill } from '../../components/layout/ListToolbar';
import PruhUkolu from '../../components/layout/PruhUkolu';
import NabidkaAkci from '../../components/layout/NabidkaAkci';
import FlekIkona, { NapricIkona, NAPRIC_IKONY } from './FlekIkona';
import { GRID_FLEK, useViditelneSloupceFleku, fmtKc } from './flekyGrid';

const ACCENT = '#3B82F6';
const ACCENT_BG = 'rgba(59,130,246,0.12)';

/** Pohledy = stavy fleků, ne libovolný filtr. Vždy je aktivní právě jeden. */
export const POHLEDY = [
    { id: 'ceka', label: 'Čeká na schválení', upozorneni: true },
    // Diagnostika, ne stav: značku „audit starší než týden" modul na řádku už
    // kreslil, ale nedalo se podle ní filtrovat — přesně ta vada, kterou mají
    // pohledy Surovin vyřešenou („Bez ceny", „Zdražilo").
    { id: 'neauditovane', label: 'Neauditované', upozorneni: true },
    { id: 'otevrene', label: 'Otevřené' },
    { id: 'dnes', label: 'Dnes' },
    { id: 'vse', label: 'Vše' },
    { id: 'neaktivni', label: 'Neaktivní' },
];

/** Položky, které nejsou flek — pohledávky, reporty, konfigurace. */
export const NAPRIC = [
    { id: 'pohledavky', nazev: 'Pohledávky', popis: 'srážky ze mzdy za manka' },
    { id: 'reporty', nazev: 'Reporty', popis: 'dýška a manka napříč fleky' },
    { id: 'konfigurace', nazev: 'Konfigurace', popis: 'limity manka, základ rozvozu' },
];

export const STAVY = {
    otevreno: { label: 'otevřeno', barva: '#10B981' },
    ceka_na_schvaleni: { label: 'čeká na schválení', barva: '#F59E0B' },
    uzavreno: { label: 'uzavřeno', barva: null },
};

/**
 * Levý sloupec modulu Pokladna — fleky jako položky.
 *
 * Kostra je řádek Spižírny: miniatura v barvě, název s metařádkem pod ním,
 * vpravo sloupce s čísly. Dvě hustoty — `siroky` je přehled přes celou šířku,
 * úzký je navigace vedle detailu.
 *
 * Fleků jsou čtyři, ne čtyři sta. Přehled proto nesmí být jen seznam jmen —
 * nese dnešní čísla, takže je zároveň dashboardem modulu a nikdo nemusí hledat
 * KPI pásek nad ním. Skupiny drží součty ve stejných sloupcích jako položky,
 * stejně jako kategorie u surovin.
 */
export default function FlekyListPanel({
    siroky = false, kompaktni = false, isDark,
    fleky = [], vsechnyFleky = [], souhrn = null, loading = false,
    hledani, setHledani, pohled, setPohled,
    vybranoId, vybranoNapric, onVyber, onVyberNapric, onNovyFlek,
    cardStyle, mutedColor, secondaryColor, inputStyle, rezervaDole = null,
}) {
    const sloupce = useViditelneSloupceFleku();
    const hlavni = fleky.filter(f => f.typ === 'bistro_hlavni');
    const rozvoz = fleky.filter(f => f.typ === 'rozvoz');
    const soucet = (list, klic) => list.reduce((a, f) => a + Number(f[klic] ?? 0), 0);

    // Počty se berou z celé sady, ne z právě zobrazené — pilulka jinak hlásí
    // jen to, co je pod ní vidět, a přestane být užitečná.
    const vsechny = vsechnyFleky.length ? vsechnyFleky : fleky;
    const pocty = {
        otevrene: vsechny.filter(f => f.smena?.stav === 'otevreno').length,
        ceka: vsechny.filter(f => f.smena?.stav === 'ceka_na_schvaleni').length,
        neauditovane: vsechny.filter(f => f.aktivni && f.priznak === 'audit_stary').length,
        dnes: vsechny.filter(f => f.pohybu_dnes > 0).length,
        vse: vsechny.filter(f => f.aktivni).length,
        neaktivni: vsechny.filter(f => !f.aktivni).length,
    };

    const skupina = (nazev, list) => list.length > 0 && (
        <>
            <SkupinaRadek nazev={nazev} siroky={siroky} sloupce={sloupce}
                hotovost={soucet(list, 'hotovost_kc')} trzba={soucet(list, 'trzba_dnes_kc')}
                pohyby={soucet(list, 'pohybu_dnes')} isDark={isDark} mutedColor={mutedColor} />
            {list.map(f => (
                <FlekRadek key={f.id} flek={f} siroky={siroky} sloupce={sloupce}
                    aktivni={vybranoId === f.id} onClick={() => onVyber(f.id)}
                    isDark={isDark} mutedColor={mutedColor} />
            ))}
        </>
    );

    return (
        /* Kartu shazuje jen mobil (`kompaktni`), ne široký přehled. Sklo
           roztažené přes celý displej rozmaže brandovou záři pod sebou a jeho
           hrana se čte jako předěl; na desktopu ale seznam nad stránkou
           plave — s kartou i v přehledu, stejně jako Suroviny. */
        <div className={`overflow-hidden flex flex-col flex-1 min-h-0 ${kompaktni ? '' : 'rounded-xl'}`}
            style={kompaktni ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={kompaktni} isDark={isDark}
                mutedColor={mutedColor} inputStyle={inputStyle} barva={ACCENT}
                hledani={{
                    value: hledani, onChange: setHledani,
                    placeholder: 'Hledat flek nebo držitele', placeholderKratky: 'Hledat',
                }}
                akce={[{ id: 'novy', label: 'Otevřít flek', Icon: Plus, onClick: onNovyFlek, primarni: true }]}
                nastroje={(
                    /* Nástroje modulu k akcím, ne na konec seznamu — ať je
                       návyk stejný jako v Dokladech, kde se pod stovkami
                       řádků k takové skupině nikdo nedoroluje. */
                    <NabidkaAkci isMobile={kompaktni} isDark={isDark} barva={ACCENT} kolikVidet={0}
                        akce={NAPRIC.map(p => ({
                            id: p.id, label: p.nazev, Icon: NAPRIC_IKONY[p.id],
                            onClick: () => onVyberNapric(p.id),
                        }))} />
                )}
                pohled={POHLEDY.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]}
                        aktivni={pohled === p.id} upozorneni={p.upozorneni}
                        onClick={() => setPohled(p.id)}
                        barva={ACCENT} isDark={isDark} mutedColor={mutedColor} kompaktni={kompaktni} />
                ))}
            />

            {/* Co je potřeba udělat — cockpit tohle umí („Zbývají 3 kontroly"),
                pokladna to dosud říkala jen oranžovým trojúhelníčkem na řádku. */}
            <div style={{ padding: kompaktni ? '8px 10px 0' : '8px 10px 0' }}>
                <PruhUkolu isDark={isDark} kompaktni={kompaktni} ukoly={[
                    {
                        id: 'ceka', pocet: pocty.ceka, barva: '#F59E0B',
                        text: pocty.ceka === 1 ? 'rozvozce čeká na vyrovnání' : 'rozvozci čekají na vyrovnání',
                        onClick: () => setPohled('ceka'),
                    },
                    {
                        id: 'audit', pocet: pocty.neauditovane, barva: '#EF4444',
                        text: pocty.neauditovane === 1 ? 'flek se týden nepřepočítal' : 'fleky se týden nepřepočítaly',
                        onClick: () => setPohled('neauditovane'),
                    },
                ]} />
            </div>

            {siroky && <HlavickaMrizky sloupce={sloupce} mutedColor={mutedColor} />}

            <div className="flex-1 overflow-y-auto" style={rezervaDole ? { paddingBottom: rezervaDole } : null}>
                {loading && <div className="px-3 py-6 text-xs" style={{ color: mutedColor }}>Načítám…</div>}

                {!loading && skupina('Hlavní pokladna', hlavni)}
                {!loading && skupina(`Rozvozové (${rozvoz.length})`, rozvoz)}

                {!loading && fleky.length === 0 && (
                    <div className="px-4 py-10 text-center">
                        <div className="text-[12.5px]" style={{ color: 'var(--text-primary)' }}>
                            {hledani ? `Nic nenalezeno pro „${hledani}"` : 'V tomhle pohledu není žádný flek'}
                        </div>
                        <div className="text-[11px] mt-1" style={{ color: mutedColor }}>
                            {hledani ? 'Zkus jiné jméno nebo přepni pohled.' : 'Přepni pohled na Vše.'}
                        </div>
                    </div>
                )}
            </div>

            {/* Souhrn celého provozu — jen v přehledu, kde je na něj místo. */}
            {siroky && souhrn && (
                <div className="flex items-center gap-5 text-[11.5px] tabular-nums"
                    style={{
                        padding: '9px 12px',
                        borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                        color: mutedColor,
                    }}>
                    <span>Hotovost celkem <strong style={{ color: 'var(--text-primary)' }}>{fmtKc(souhrn.hotovost_kc)}</strong></span>
                    <span>Týden <strong style={{ color: souhrn.tyden_saldo_kc >= 0 ? '#10B981' : '#EF4444' }}>
                        {souhrn.tyden_saldo_kc >= 0 ? '+' : ''}{fmtKc(souhrn.tyden_saldo_kc)}
                    </strong></span>
                    <span>{souhrn.ceka_na_schvaleni} čeká na schválení</span>
                    <span>{souhrn.pohledavky_otevrene} pohledávek</span>
                </div>
            )}
        </div>
    );
}

function HlavickaMrizky({ sloupce, mutedColor }) {
    const bunka = (label, sirka) => (
        <div style={{ width: sirka, flexShrink: 0, textAlign: 'right' }}>{label}</div>
    );
    return (
        <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide"
            style={{ padding: '6px 10px', gap: GRID_FLEK.mezera, color: mutedColor }}>
            <div style={{ width: GRID_FLEK.uchyt, flexShrink: 0 }} />
            <div style={{ flex: `${GRID_FLEK.polozkaRust} 1 0`, minWidth: GRID_FLEK.polozkaMin }}>Flek</div>
            {sloupce.drzitel && <div style={{ width: GRID_FLEK.drzitel, flexShrink: 0 }}>Držitel</div>}
            {bunka('Hotovost', GRID_FLEK.hotovost)}
            {bunka('Tržba dnes', GRID_FLEK.trzba)}
            {sloupce.pohyby && bunka('Pohyby', GRID_FLEK.pohyby)}
            {sloupce.audit && bunka('Audit', GRID_FLEK.audit)}
            <div style={{ width: GRID_FLEK.stav, flexShrink: 0 }} />
        </div>
    );
}

/** Skupina nese součty ve stejných sloupcích jako položky pod ní. */
function SkupinaRadek({ nazev, siroky, sloupce, hotovost, trzba, pohyby, isDark, mutedColor }) {
    const hranice = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    if (!siroky) {
        return (
            <div className="text-[10.5px] font-semibold uppercase tracking-wide"
                style={{ padding: '12px 10px 5px', color: mutedColor }}>{nazev}</div>
        );
    }
    const bunka = (text, sirka) => (
        <div className="tabular-nums" style={{ width: sirka, flexShrink: 0, textAlign: 'right' }}>{text}</div>
    );
    return (
        <div className="flex items-center text-[11.5px] font-semibold"
            style={{
                padding: '7px 10px', gap: GRID_FLEK.mezera,
                borderTop: `1px solid ${hranice}`, borderBottom: `1px solid ${hranice}`,
                color: 'var(--text-secondary)',
            }}>
            <div style={{ width: GRID_FLEK.uchyt, flexShrink: 0 }} />
            <div style={{ flex: `${GRID_FLEK.polozkaRust} 1 0`, minWidth: GRID_FLEK.polozkaMin }}>{nazev}</div>
            {sloupce.drzitel && <div style={{ width: GRID_FLEK.drzitel, flexShrink: 0 }} />}
            {bunka(hotovost != null ? fmtKc(hotovost) : '', GRID_FLEK.hotovost)}
            {bunka(trzba != null ? fmtKc(trzba) : '', GRID_FLEK.trzba)}
            {sloupce.pohyby && bunka(pohyby != null ? String(pohyby) : '', GRID_FLEK.pohyby)}
            {sloupce.audit && <div style={{ width: GRID_FLEK.audit, flexShrink: 0 }} />}
            <div style={{ width: GRID_FLEK.stav, flexShrink: 0 }} />
        </div>
    );
}

/** Podklad řádku — stejná trojice stavů jako u Spižírny (vybráno / hover / nic). */
function podkladRadku(aktivni, isDark) {
    return aktivni ? (isDark ? 'rgba(59,130,246,0.14)' : 'rgba(59,130,246,0.08)') : 'transparent';
}

function FlekRadek({ flek, siroky, sloupce, aktivni, onClick, isDark, mutedColor }) {
    const stav = STAVY[flek.smena?.stav] ?? null;
    const podklad = podkladRadku(aktivni, isDark);

    const spolecne = {
        'data-list-id': flek.id,
        onClick,
        className: 'flex items-center cursor-pointer transition-colors w-full text-left',
        style: {
            gap: siroky ? GRID_FLEK.mezera : 10,
            padding: siroky ? '7px 10px' : '8px 10px',
            borderLeft: `3px solid ${aktivni ? ACCENT : 'transparent'}`,
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            background: podklad,
            opacity: flek.aktivni === false ? 0.6 : 1,
            userSelect: 'none',
        },
        onMouseEnter: e => { if (!aktivni) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)'; },
        onMouseLeave: e => { if (!aktivni) e.currentTarget.style.background = podklad; },
    };

    const znacka = (
        <>
            {flek.priznak === 'ceka_na_schvaleni' && <AlertTriangle size={14} style={{ color: '#F59E0B' }} />}
            {flek.priznak === 'audit_stary' && <CircleDot size={12} style={{ color: '#EF4444' }} />}
        </>
    );

    // ── Přehled: řádek sloupcové mřížky ──
    if (siroky) {
        const bunka = (text, sirka, zvyrazni = false) => (
            <div className="tabular-nums truncate" style={{
                width: sirka, flexShrink: 0, textAlign: 'right',
                color: zvyrazni ? 'var(--text-primary)' : mutedColor,
            }}>{text}</div>
        );
        return (
            <div {...spolecne}>
                <div style={{ width: GRID_FLEK.uchyt, flexShrink: 0, display: 'flex' }}>
                    <FlekIkona flek={flek} size={40} radius={10} />
                </div>
                <div className="min-w-0" style={{ flex: `${GRID_FLEK.polozkaRust} 1 0`, minWidth: GRID_FLEK.polozkaMin }}>
                    <div className="flex items-baseline gap-2 min-w-0">
                        <span className="text-[13px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{flek.nazev}</span>
                        {flek.aktivni === false && (
                            <span className="text-[10px] font-medium flex-shrink-0" style={{ color: '#EF4444' }}>neaktivní</span>
                        )}
                    </div>
                    <div className="text-[11px] truncate" style={{ color: stav?.barva ?? mutedColor, marginTop: 1 }}>
                        {stav?.label ?? 'volný'}
                    </div>
                </div>
                {sloupce.drzitel && (
                    <div className="truncate text-[12.5px]" style={{
                        width: GRID_FLEK.drzitel, flexShrink: 0,
                        color: flek.drzitel ? 'var(--text-primary)' : mutedColor,
                    }}>{flek.drzitel ?? '—'}</div>
                )}
                <div className="text-[12.5px] tabular-nums font-semibold" style={{ width: GRID_FLEK.hotovost, flexShrink: 0, textAlign: 'right', color: 'var(--text-primary)' }}>
                    {fmtKc(flek.hotovost_kc)}
                </div>
                {bunka(flek.trzba_dnes_kc ? fmtKc(flek.trzba_dnes_kc) : '—', GRID_FLEK.trzba, !!flek.trzba_dnes_kc)}
                {sloupce.pohyby && bunka(String(flek.pohybu_dnes ?? 0), GRID_FLEK.pohyby)}
                {sloupce.audit && bunka(flek.posledni_audit?.datum ?? '—', GRID_FLEK.audit)}
                <div className="flex items-center justify-end" style={{ width: GRID_FLEK.stav, flexShrink: 0 }}>{znacka}</div>
            </div>
        );
    }

    // ── Úzký sloupec vedle detailu: dvouřádková karta ──
    return (
        <div {...spolecne}>
            <FlekIkona flek={flek} size={36} />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold flex-1 min-w-0 truncate" style={{ color: 'var(--text-primary)' }}>{flek.nazev}</span>
                    <span className="flex items-center" style={{ flexShrink: 0 }}>{znacka}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    {/* Stav nese tečka, ne barva jména — zbarvené jméno vypadá,
                        jako by něco hlásil člověk, ne flek. */}
                    <span className="text-[11px] flex-1 min-w-0 truncate flex items-center gap-1.5" style={{ color: mutedColor }}>
                        {stav?.barva && (
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: stav.barva, flexShrink: 0 }} />
                        )}
                        {flek.drzitel ?? stav?.label ?? 'volný'}
                    </span>
                    <span className="text-[12px] font-bold tabular-nums" style={{ color: 'var(--text-primary)', flexShrink: 0 }}>
                        {fmtKc(flek.hotovost_kc)}
                    </span>
                </div>
            </div>
        </div>
    );
}
